// web-staking-estimator-frontend/src/hooks/useSubmitActions.js
import { useState, useCallback } from "react";
import { downloadPdfBlob } from "../services/pdfService";
import { missingForGenerateMember } from "../utils/validation";

/**
 * Custom hook to handle estimate generation and service request submission
 * @param {object} params - Hook parameters
 * @param {Function} params.ensureSaved - Function to ensure estimate is saved
 * @param {object} params.member - Member information
 * @param {string} params.prettyEstimateId - Pretty estimate ID
 * @param {Function} params.getMissingForEstimate - Function to get missing estimate fields
 * @param {Function} params.getMissingForRequest - Function to get missing request fields
 * @param {object} params.uiCfg - UI configuration
 * @param {Function} params.setSubmitMsg - Function to set submit message
 * @param {Function} params.setEstimateErr - Function to set estimate error
 * @param {Function} params.setEstimate - Function to update estimate state
 * @returns {object} Hook utilities and state
 */
export function useSubmitActions({
  ensureSaved,
  member,
  prettyEstimateId,
  getMissingForEstimate,
  getMissingForRequest,
  uiCfg,
  setSubmitMsg,
  setEstimateErr,
  setEstimate,
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Handle PDF estimate generation
   */
  const handleGenerateEstimate = useCallback(async () => {
    if (isGenerating) return;

    const missing = [
      ...getMissingForEstimate(),
      ...missingForGenerateMember(member),
    ];

    if (missing.length) {
      setEstimateErr(
        (uiCfg?.messages?.missingInputsPrefix ||
          "Please add the missing inputs: ") + missing.join(", ")
      );
      return;
    }

    setEstimateErr(null);
    setIsGenerating(true);

    try {
      /*
       * Capture map screenshot BEFORE saving
       * Timing issue: if we save first, map might re-render and screenshot captures wrong state
       * Pass screenshot directly to ensureSaved to avoid React state update delays
       */
      let mapImageDataUrl = null;
      if (typeof window !== "undefined" && window.__CAPTURE_MAP_SNAPSHOT) {
        if (process.env.NODE_ENV === 'development') {
          console.log("[Generate] Capturing map snapshot for PDF...");
        }
        try {
          mapImageDataUrl = await window.__CAPTURE_MAP_SNAPSHOT();
        } catch (snapshotErr) {
          console.error("[Generate] Screenshot capture failed:", snapshotErr);
          // Continue anyway - backend may handle missing screenshot gracefully
        }
      }

      // Pass screenshot directly to ensureSaved (bypasses state timing issues)
      const { id, prettyId } = await ensureSaved(mapImageDataUrl);
      if (!id) throw new Error("No estimate id after save");

      /*
       * Best-effort email to user
       * Don't block PDF download if email fails (SMTP server might be down)
       * User can always re-email from admin panel later
       */
      try {
        await fetch(`/api/email/${id}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to: (member.email || "").trim() || undefined,
          }),
        });
      } catch (e) {
        console.warn("Email failed:", e);
      }

      // Filename now determined by server's Content-Disposition header
      // Fallback used only if header is missing
      await downloadPdfBlob(id, `estimate-${prettyId ?? prettyEstimateId ?? id}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Unable to generate estimate. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  }, [
    isGenerating,
    getMissingForEstimate,
    member,
    uiCfg,
    setEstimateErr,
    setEstimate,
    ensureSaved,
    prettyEstimateId,
  ]);

  /**
   * Handle service request submission
   */
  const handleStartServiceRequest = useCallback(async () => {
    try {
      const miss = getMissingForRequest();
      if (miss.length) {
        setSubmitMsg({
          type: "err",
          text:
            (uiCfg?.messages?.missingInputsPrefix ||
              "Please add the missing inputs: ") + miss.join(", "),
        });
        return;
      }

      setIsSubmitting(true);
      // Ensure we have a saved estimate id & pdf snapshot available
      const { id } = await ensureSaved();
      if (!id) throw new Error("No estimate id after save");

      const res = await fetch(
        `/api/email/start-request/${encodeURIComponent(id)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}",
        }
      );
      if (!res.ok) throw new Error("send failed");

      /*
       * Success message with optional member portal link
       * Base message: "Request submitted, rep will contact you"
       * If portal configured: append "For updates, click here (url)"
       * Portal URL/message are optional in config
       */
      setSubmitMsg({
        type: "ok",
        text:
          (() => {
            const baseMsg = uiCfg?.messages?.requestSubmitted || "Your request has been submitted, a representative will be in touch with you once the request has been reviewed.";
            const portalMsg = uiCfg?.messages?.requestSubmittedMemberPortal;
            const url = uiCfg?.memberPortal?.applicationUrl;
            const linkText = uiCfg?.memberPortal?.applicationLinkText || "click here";
            
            // If we have both portal message and URL, append them
            if (portalMsg && url) {
              return `${baseMsg} ${portalMsg.replace(/click here/i, `${linkText} (${url})`)}`;
            }
            return baseMsg;
          })(),
      });
      // Message persists until user changes form inputs or clicks Start Over
    } catch {
      setSubmitMsg({
        type: "err",
        text:
          uiCfg?.messages?.requestFailed ||
          "We couldn't send your request right now. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    getMissingForRequest,
    uiCfg,
    setSubmitMsg,
    ensureSaved,
  ]);

  return {
    isGenerating,
    isSubmitting,
    handleGenerateEstimate,
    handleStartServiceRequest,
  };
}
